<template>
  <!-- 人员选择器:左侧组织树整选 + 右侧分页列表逐人勾选,已选用「范围条目」表达,避免渲染上千个标签 -->
  <div class="person-picker">
    <!-- 表单内的摘要入口(详情模式下按钮为「查看名单」,只读浏览) -->
    <div class="pp-trigger">
      <!-- 详情模式(el-form disabled)下仍要能「查看名单」,只读由本组件的 disabled 控制 -->
      <el-button type="primary" plain size="small" @click="openDialog">
        {{ disabled ? '查看名单' : (value.length ? `重新选择${unitName}` : `选择${unitName}`) }}
      </el-button>
      <span class="pp-summary" v-if="value.length">已选 {{ value.length }} 人</span>
      <el-button type="text" size="small" v-if="value.length && !disabled" @click="clearAll">清空</el-button>
      <div class="pp-name-tags" v-if="visibleNames.length">
        <el-tag v-for="(name, idx) in visibleNames" :key="idx" size="mini">{{ name }}</el-tag>
        <el-tag v-if="value.length > visibleNames.length" size="mini" type="info">等 {{ value.length }} 人</el-tag>
      </div>
    </div>

    <el-dialog :visible.sync="visible" :title="disabled ? `${unitName}名单` : `选择${unitName}`" width="900px"
      :fullscreen="isMobile" :close-on-click-modal="false" append-to-body @open="handleDialogOpen">
      <div class="pp-wrap" :class="{ 'pp-mobile': isMobile }">
        <!-- 移动端:组织级联选择(院系 → 专业 → 班级,选到任意一级即可) -->
        <div class="pp-mobile-bar" v-if="isMobile">
          <el-cascader ref="mobileCascader" v-model="mobileNodeKey" :options="cascaderOptions" :props="cascaderProps"
            size="small" clearable :append-to-body="true" popper-class="pp-cascader-popper"
            placeholder="选择院系 / 专业 / 班级" @change="handleMobileNodeChange"></el-cascader>
        </div>

        <div class="pp-body">
          <!-- 桌面:组织树 -->
          <div class="pp-tree" v-if="!isMobile">
            <el-input v-model="treeKeyword" size="mini" placeholder="搜索院系 / 专业 / 班级" clearable></el-input>
            <div class="pp-tree-list">
              <el-tree ref="tree" :data="treeData" node-key="key" :show-checkbox="!disabled" check-strictly
                :props="treeProps" :filter-node-method="filterTreeNode" :expand-on-click-node="false" default-expand-all
                @check="handleTreeCheck" @node-click="handleTreeNodeClick">
                <span class="pp-tree-node" slot-scope="{ data }">{{ data.name }}</span>
              </el-tree>
              <div class="pp-tree-empty" v-if="!treeData.length">组织结构为空</div>
            </div>
          </div>

          <!-- 右:人员列表 -->
          <div class="pp-list">
            <div class="pp-list-head">
              <span class="pp-scope">{{ currentNode ? currentNode.name : `全部${unitName}` }}</span>
              <el-input v-model="keyword" size="mini" class="pp-search" :placeholder="`搜索姓名 / ${numberLabel}`"
                clearable @keyup.enter.native="handleSearch" @clear="handleSearch"></el-input>
              <el-button size="mini" @click="handleSearch">搜索</el-button>
            </div>
            <div class="pp-actions">
              <span class="pp-total">共 {{ total }} 人</span>
              <el-checkbox v-model="onlyPicked" @change="handleOnlyPickedChange">只看已选</el-checkbox>
              <template v-if="!disabled">
                <el-button size="mini" @click="selectCurrentPage">全选本页</el-button>
                <el-button size="mini" @click="clearCurrentPage">清除本页</el-button>
                <el-button size="mini" type="primary" :loading="scopeLoading" :disabled="!currentNode && !keyword"
                  @click="handleScopeAll">
                  {{ scopeButtonText }}
                </el-button>
              </template>
            </div>
            <div class="pp-rows" v-loading="listLoading">
              <div class="pp-row pp-row-head">
                <span class="pp-cell-check"></span>
                <span class="pp-cell-name">姓名</span>
                <span class="pp-cell-num">{{ numberLabel }}</span>
              </div>
              <div class="pp-row" v-for="row in visibleRows()" :key="row.id" :class="{ 'is-picked': isPicked(row) }">
                <span class="pp-cell-check">
                  <el-checkbox :value="isPicked(row)" :disabled="disabled"
                    @change="val => toggleRow(row, val)"></el-checkbox>
                </span>
                <span class="pp-cell-name">{{ row.name }}</span>
                <span class="pp-cell-num">{{ row.number }}</span>
              </div>
              <div class="pp-row-empty" v-if="!listLoading && !visibleRows().length">
                {{ onlyPicked ? '当前页没有已选人员' : '没有符合条件的数据' }}
              </div>
            </div>
            <div class="pp-pager">
              <el-pagination small background layout="total, prev, pager, next" :current-page="page" :page-size="size"
                :total="total" @current-change="handlePageChange"></el-pagination>
            </div>
          </div>
        </div>

        <!-- 已选区:一个范围 = 一条标签 -->
        <div class="pp-picked">
          <div class="pp-picked-head">
            已选 <b>{{ finalCount }}</b> 人
            <span class="pp-excluded" v-if="excludes.length">（已排除 {{ excludes.length }} 人）</span>
          </div>
          <div class="pp-picked-body">
            <el-tag v-for="sc in scopes" :key="sc.sig" size="small" type="success" closable
              @close="removeScope(sc.sig)">
              {{ sc.label }} · {{ sc.loading ? '获取中…' : `${scopeCount(sc)} 人` }}
            </el-tag>
            <template v-if="!isLegacyBulk">
              <el-tag v-for="item in visibleIndividuals" :key="item.id" size="small" closable
                @close="removeIndividual(item.id)">
                {{ item.name }}
              </el-tag>
              <el-tag v-if="individuals.length > visibleIndividuals.length" size="small" type="info">
                等 {{ individuals.length }} 人
              </el-tag>
            </template>
            <span class="pp-picked-empty" v-else>
              已选 {{ individuals.length }} 人(名单未含姓名,可清空后重新按组织整选)
            </span>
            <span class="pp-picked-empty" v-if="!scopes.length && !individuals.length">
              勾选左侧组织即整选该范围(院系 / 专业 / 班级),也可在右侧逐人勾选
            </span>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="visible = false">{{ disabled ? '关 闭' : '取 消' }}</el-button>
        <el-button v-if="!disabled" type="primary" @click="handleConfirm">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 详情模式:直接看只读名单(走 *_ChooseList 接口),不进选择器 -->
    <person-roster-dialog v-if="disabled" ref="rosterDialog" :type="type" :course-id="courseId">
    </person-roster-dialog>
  </div>
</template>

<script>
import PersonRosterDialog from './personRosterDialog.vue';

export default {
  name: 'PersonPicker',
  components: {
    PersonRosterDialog,
  },
  props: {
    // student 学生 / teacher 老师
    type: {
      type: String,
      default: 'student',
    },
    // 课程 id:详情模式「查看名单」按课程拉已选人员
    courseId: {
      type: [String, Number],
      default: '',
    },
    // 已选 id 数组(父表单字段,如 student_ids)
    value: {
      type: Array,
      default: () => [],
    },
    // 已选名称数组,与 value 顺序对应(编辑回显用,来自列表 desc 字段)
    names: {
      type: Array,
      default: () => [],
    },
    // 表单当前组织结构,作为打开时的默认筛选范围
    org1: {
      type: String,
      default: '',
    },
    org2: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  // 断开外层 el-form 的 disabled 注入:课程详情页整表单只读,但这里要能点开「查看名单」、
  // 弹窗内还要能搜索/翻页;是否只读完全由本组件的 disabled 控制
  provide() {
    return { elForm: { disabled: false } };
  },
  data() {
    return {
      visible: false,
      isMobile: false,
      treeData: [],
      treeProps: { children: 'sub', label: 'name' },
      treeKeyword: '',
      flatNodes: [],
      currentNode: null,
      mobileNodeKey: '',
      keyword: '',
      onlyPicked: false,
      list: [],
      total: 0,
      page: 1,
      size: 20,
      listLoading: false,
      scopeLoading: false,
      // 已选 = 组织范围条目 + 个别人 - 排除名单
      scopes: [],
      excludes: [],
      individuals: [],
      excludeSet: new Set(),
      individualMap: new Map(),
      scopeSet: new Set(),
      finalIds: [],
      finalCount: 0,
      syncingTree: false,
    };
  },
  computed: {
    isTeacher() {
      return this.type === 'teacher';
    },
    unitName() {
      return this.isTeacher ? '老师' : '学生';
    },
    numberLabel() {
      return this.isTeacher ? '工号' : '学号';
    },
    listUrl() {
      return this.isTeacher ? 'api/new_course/teacherList' : 'api/new_course/studentList';
    },
    idsUrl() {
      return this.isTeacher ? 'api/new_course/teacherIds' : 'api/new_course/studentIds';
    },
    // 移动端级联选择:组织树转 cascader options(value 用节点 key,便于 findNode 定位)
    cascaderOptions() {
      const build = nodes => (nodes || []).map(node => {
        const item = { value: node.key, label: node.name };
        const children = build(node.sub);
        if (children.length) item.children = children;
        return item;
      });
      // 顶层「学校」节点(没有 level)没得选,直接用它的下级(院系)作为第一级,省一级宽度
      const roots = this.treeData.length === 1 && !this.treeData[0].level
        ? this.treeData[0].sub
        : this.treeData;
      return build(roots);
    },
    cascaderProps() {
      return {
        value: 'value',
        label: 'label',
        children: 'children',
        expandTrigger: 'click', // 移动端没有 hover,点击展开下一级
        checkStrictly: true, // 选到院系/专业/班级任意一级都算数
        emitPath: false, // v-model 直接是节点 key
      };
    },
    // 名称去重:整选出来的人回显时都是同一个范围名,重复展示没有意义
    visibleNames() {
      const unique = [];
      (this.names || []).forEach(name => {
        if (name && unique.indexOf(name) === -1) unique.push(name);
      });
      return unique.slice(0, 3);
    },
    // 只有拿到姓名的才逐个展示;没有姓名时不铺开一排 id,只给人数
    visibleIndividuals() {
      return this.individuals.filter(item => item.name).slice(0, 6);
    },
    // 历史数据只有 id、拿不到姓名:不逐个展示标签,只显示人数
    isLegacyBulk() {
      if (this.scopes.length || !this.individuals.length) return false;
      if (this.individuals.length > 20) return true;
      return this.individuals.every(item => !item.name);
    },
    scopeButtonText() {
      if (!this.currentNode && !this.keyword) return '整选(请先选择组织)';
      const name = this.currentNode ? this.currentNode.name : '搜索结果';
      return `整选${name}(${this.total})`;
    },
  },
  mounted() {
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth < 768;
    },
    // ============ 打开 / 关闭 ============
    openDialog() {
      // 详情(只读)模式:直接弹出只读名单,不进选择器
      if (this.disabled) {
        if (this.$refs.rosterDialog) this.$refs.rosterDialog.open();
        return;
      }
      this.visible = true;
    },
    // 上层表单弹窗打开/关闭时收起子弹窗(弹窗套弹窗必须显式收起)
    close() {
      this.visible = false;
    },
    handleDialogOpen() {
      this.resetState();
      this.buildTree();
      this.page = 1;
      this.keyword = '';
      this.treeKeyword = '';
      this.onlyPicked = false;
      this.currentNode = null;
      this.mobileNodeKey = '';
      this.list = [];
      this.total = 0;
      this.$nextTick(() => {
        this.locateDefaultNode();
        this.syncTreeChecked();
        this.getList();
      });
    },
    resetState() {
      this.scopes = [];
      this.excludes = [];
      this.individuals = [];
      this.excludeSet = new Set();
      this.individualMap = new Map();
      this.scopeSet = new Set();
      this.finalIds = [];
      this.finalCount = 0;
      // 编辑回显:历史数据只有 id 串,无法还原当时的整选范围,统一按个人展示(折叠显示)
      const ids = this.value || [];
      const names = this.names || [];
      this.individuals = ids.map((id, i) => ({
        id: String(id),
        name: names[i] || '',
      }));
      this.individuals.forEach(item => this.individualMap.set(item.id, item));
      this.refreshFinal();
    },
    // ============ 组织结构树 ============
    buildTree() {
      const organs = this.$store.state.organs;
      let nodes = this.buildNodes(organs);
      // 树范围跟随表单「所属组织结构」:表单为空=全校,不为空=该组织及其子级
      if (this.org1) {
        const scoped = this.scopeTree(nodes, this.org1, this.org2);
        if (scoped) nodes = [scoped];
      }
      this.treeData = nodes;
      const flat = [];
      const walk = (list, depth) => {
        (list || []).forEach(node => {
          flat.push({
            key: node.key,
            label: `${'　'.repeat(depth)}${node.name}${node.level === 1 ? '(院系)' : node.level === 2 ? '(专业)' : node.level === 3 ? '(班级)' : ''}`,
          });
          walk(node.sub, depth + 1);
        });
      };
      walk(this.treeData, 0);
      this.flatNodes = flat;
    },
    // 按表单组织结构裁剪:structure_1=院系 → 该院系;structure_1+structure_2 → 该专业
    scopeTree(nodes, dep, major) {
      const depNode = this.findByLevelName(nodes, 1, dep);
      if (!depNode) return null;
      if (!major) return depNode;
      const majorNode = this.findByLevelName(depNode.sub, 2, major);
      return majorNode || depNode;
    },
    findByLevelName(nodes, level, name) {
      let hit = null;
      (nodes || []).forEach(node => {
        if (hit) return;
        if (node.level === level && node.name === name) {
          hit = node;
          return;
        }
        hit = this.findByLevelName(node.sub, level, name);
      });
      return hit;
    },
    // 保留 level 1院系 / level 2专业 / level 3班级(依次对应 structure_1、structure_2、structure_3);
    // 顶层「学校」节点没有 level,保留作为树的入口。
    // 参数取后端给的 structure_names(如 "技术系-技术开发-软件1班"),不靠节点名拼接
    buildNodes(list) {
      const out = [];
      (list || []).forEach(item => {
        if (item.level && [1, 2, 3].indexOf(item.level) === -1) return;
        const pathNames = String(item.structure_names || '').split('-').filter(Boolean);
        const node = {
          key: item.structure_names || item.name,
          name: item.name,
          level: item.level,
          params: {
            structure_1: pathNames[0] || '',
            structure_2: pathNames[1] || '',
            structure_3: pathNames[2] || '',
          },
          sub: this.buildNodes(item.sub),
        };
        out.push(node);
      });
      return out;
    },
    filterTreeNode(value, data) {
      if (!value) return true;
      return data.name.indexOf(value) !== -1;
    },
    locateDefaultNode() {
      if (!this.org1 && !this.org2) return;
      const dep = this.org1;
      const major = this.org2;
      let depNode = null;
      let majorNode = null;
      const walk = nodes => {
        (nodes || []).forEach(node => {
          if (!majorNode && node.name === dep && node.level === 1) depNode = node;
          if (!majorNode && major && node.name === major && node.level === 2) majorNode = node;
          walk(node.sub);
        });
      };
      walk(this.treeData);
      const target = majorNode || depNode;
      if (target) {
        this.currentNode = target;
        this.mobileNodeKey = target.key;
      }
    },
    handleTreeNodeClick(data) {
      this.focusNode(data);
    },
    // 聚焦某个组织节点并刷新右侧列表(点节点与勾选 checkbox 共用)
    focusNode(data) {
      this.currentNode = data;
      this.mobileNodeKey = data.key;
      this.page = 1;
      this.getList();
    },
    handleMobileNodeChange(key) {
      // 选中后收起级联面板(点单选项会冒泡到节点触发展开,面板不会自动关)
      const cascader = this.$refs.mobileCascader;
      if (cascader && cascader.toggleDropDownVisible) cascader.toggleDropDownVisible(false);
      // 清空选择:回到表单组织结构对应的范围
      if (!key) {
        this.currentNode = null;
        this.page = 1;
        this.getList();
        return;
      }
      const target = this.findNode(key);
      if (target) this.handleTreeNodeClick(target);
    },
    findNode(key, nodes) {
      const list = nodes || this.treeData;
      for (let i = 0; i < list.length; i++) {
        if (list[i].key === key) return list[i];
        const hit = this.findNode(key, list[i].sub);
        if (hit) return hit;
      }
      return null;
    },
    // ============ 整选范围 ============
    handleTreeCheck(data) {
      if (this.syncingTree) return;
      // 勾选/取消勾选都让右侧列表跟着切到该组织,勾完立刻能看到这个组织下有哪些人
      this.focusNode(data);
      // 与「整选当前组织」按钮产生的范围按同一签名判断,避免出现两条等价范围
      const sig = JSON.stringify(data.params);
      if (this.scopes.some(s => s.sig === sig)) {
        this.removeScope(sig);
        return;
      }
      // 勾选学校根节点 = 不限院系专业,等于整选全校,量大先确认
      if (!data.params.structure_1) {
        this.$confirm('该节点不限院系/专业,将整选全校人员,是否继续?', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        })
          .then(() => this.addScope(data.key, data.name, data.params))
          .catch(() => this.syncTreeChecked());
        return;
      }
      this.addScope(data.key, data.name, data.params);
    },
    handleScopeAll() {
      const params = this.currentParams();
      const label = this.currentNode ? this.currentNode.name : (this.keyword ? `搜索结果(${this.keyword})` : `全部${this.unitName}`);
      // 未限定范围时不允许整选,避免误选全校人员
      if (!this.currentNode && !this.keyword) {
        this.$message({ type: 'warning', center: true, message: '请先在左侧选择院系/专业/班级,或输入搜索条件' });
        return;
      }
      // 整选是大范围操作,人多时先确认一次
      if (this.total >= 500) {
        this.$confirm(`将整选「${label}」共 ${this.total} 人,是否继续?`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        })
          .then(() => this.addScope('', label, params))
          .catch(() => { });
        return;
      }
      this.addScope('', label, params);
    },
    // 同一组织条件可能同时来自树勾选和「整选当前组织」按钮,用参数签名去重
    async addScope(key, label, params) {
      const sig = JSON.stringify(params);
      if (this.scopes.some(s => s.sig === sig)) return;
      const scope = { key: key || `scope:${sig}`, sig, label, params, ids: [], loading: true };
      this.scopes.push(scope);
      this.scopeLoading = true;
      this.refreshFinal();
      const res = await this.$api.instance.interfaceServer(this.idsUrl, 'get', this.$until.deleteNullPrototype(params),{isLoadd:false});
      const target = this.scopes.find(s => s.sig === sig);
      // 请求期间被取消/关闭弹窗:丢弃结果
      if (!target) return;
      const ids = this.parseIds(res);
      // 该范围下没有人员:不留下空标签
      if (!ids.length) {
        this.removeScope(sig);
        this.$message({ type: 'warning', center: true, message: `「${label}」下没有${this.unitName}` });
        return;
      }
      target.ids = Object.freeze(ids);
      target._idSet = null;
      target.loading = false;
      this.scopeLoading = this.scopes.some(s => s.loading);
      this.rebuildScopeSet();
      this.syncTreeChecked();
      this.refreshFinal();
    },
    removeScope(sig) {
      const idx = this.scopes.findIndex(s => s.sig === sig);
      if (idx === -1) return;
      this.scopes.splice(idx, 1);
      this.rebuildScopeSet();
      // 范围移除后不再被任何范围覆盖的排除项要清掉
      this.excludes = this.excludes.filter(id => this.scopeSet.has(id));
      this.excludeSet = new Set(this.excludes);
      this.scopeLoading = this.scopes.some(s => s.loading);
      this.syncTreeChecked();
      this.refreshFinal();
    },
    scopeCount(scope) {
      let n = 0;
      this.scopeSetOf(scope).forEach(id => {
        if (!this.excludeSet.has(id)) n++;
      });
      return n;
    },
    // 缓存范围的 id 集合:几千个 id 每次渲染都重建会明显拖慢列表
    scopeSetOf(scope) {
      if (!scope._idSet) {
        const set = new Set();
        (scope.ids || []).forEach(id => set.add(String(id)));
        scope._idSet = set;
      }
      return scope._idSet;
    },
    rebuildScopeSet() {
      const set = new Set();
      this.scopes.forEach(scope => {
        (scope.ids || []).forEach(id => set.add(String(id)));
      });
      this.scopeSet = set;
    },
    // teacherIds / studentIds 返回 { code:0, msg:'', data:[...] };
    // 实测后端返回过两种形态:[{ id: '...' }] 与 ['3', ...],统一归一成 id 字符串
    parseIds(res) {
      if (!res || res.code != 0 || !Array.isArray(res.data)) return [];
      return res.data.map(item => {
        if (item && typeof item === 'object') return String(item.id);
        return String(item);
      });
    },
    syncTreeChecked() {
      const tree = this.$refs.tree;
      if (!tree) return;
      this.syncingTree = true;
      tree.setCheckedKeys(this.scopes.map(s => s.key));
      this.$nextTick(() => {
        this.syncingTree = false;
      });
    },
    // ============ 列表 ============
    currentParams() {
      const params = this.currentNode ? Object.assign({}, this.currentNode.params) : {};
      if (!this.currentNode) {
        if (this.org1) params.structure_1 = this.org1;
        if (this.org2) params.structure_2 = this.org2;
      }
      if (this.keyword) params.keyword = this.keyword;
      return params;
    },
    async getList() {
      this.listLoading = true;
      const params = this.currentParams();
      params.page = this.page;
      params.size = this.size;
      const res = await this.$api.instance.interfaceServer(this.listUrl, 'get', this.$until.deleteNullPrototype(params),{isLoadd:false});
      this.listLoading = false;
      if (res.code == 0 && res.data) {
        this.list = res.data.data || [];
        this.total = res.data.total || 0;
      } else {
        this.list = [];
        this.total = 0;
      }
    },
    handleSearch() {
      this.page = 1;
      this.getList();
    },
    handleOnlyPickedChange() {
      // 只看已选仅对当前页做过滤,不改变服务端分页
    },
    handlePageChange(val) {
      this.page = val;
      this.getList();
    },
    // 「只看已选」按当前页过滤(用方法而非 computed,勾选后能立即反映)
    visibleRows() {
      if (!this.onlyPicked) return this.list;
      return this.list.filter(row => this.isPicked(row));
    },
    // ============ 勾选状态 ============
    isPicked(row) {
      const id = String(row.id);
      if (this.individualMap.has(id)) return true;
      if (this.excludeSet.has(id)) return false;
      return this.scopeSet.has(id);
    },
    toggleRow(row, checked) {
      const id = String(row.id);
      if (checked) {
        this.excludeSet.delete(id);
        this.excludes = this.excludes.filter(item => item !== id);
        if (!this.scopeSet.has(id) && !this.individualMap.has(id)) {
          const item = { id, name: row.name, number: row.number };
          this.individualMap.set(id, item);
          this.individuals.push(item);
        }
      } else {
        this.individualMap.delete(id);
        this.individuals = this.individuals.filter(item => item.id !== id);
        if (this.scopeSet.has(id)) {
          this.excludeSet.add(id);
          if (this.excludes.indexOf(id) === -1) this.excludes.push(id);
        }
      }
      this.refreshFinal();
    },
    // 全选本页(批量处理,避免逐行触发 refreshFinal —— 范围可能有几千个 id)
    selectCurrentPage() {
      const addIds = [];
      this.visibleRows().forEach(row => {
        const id = String(row.id);
        if (this.scopeSet.has(id) || this.individualMap.has(id)) return;
        this.excludeSet.delete(id);
        const item = { id, name: row.name, number: row.number };
        this.individualMap.set(id, item);
        this.individuals.push(item);
        addIds.push(id);
      });
      if (!addIds.length) return;
      this.excludes = this.excludes.filter(item => addIds.indexOf(item) === -1);
      this.refreshFinal();
    },
    // 清除本页选择:已选的人全部取消,落在整选范围内的记入排除名单
    clearCurrentPage() {
      const removeIds = [];
      this.visibleRows().forEach(row => {
        const id = String(row.id);
        if (!this.isPicked(row)) return;
        removeIds.push(id);
        this.individualMap.delete(id);
        if (this.scopeSet.has(id)) {
          this.excludeSet.add(id);
          if (this.excludes.indexOf(id) === -1) this.excludes.push(id);
        }
      });
      if (!removeIds.length) return;
      this.individuals = this.individuals.filter(item => removeIds.indexOf(item.id) === -1);
      this.refreshFinal();
    },
    removeIndividual(id) {
      const key = String(id);
      this.individualMap.delete(key);
      this.individuals = this.individuals.filter(item => item.id !== key);
      this.excludeSet.delete(key);
      this.excludes = this.excludes.filter(item => item !== key);
      this.refreshFinal();
    },
    clearAll() {
      this.scopes = [];
      this.excludes = [];
      this.individuals = [];
      this.excludeSet = new Set();
      this.individualMap = new Map();
      this.scopeSet = new Set();
      this.refreshFinal();
      this.emitChange();
    },
    // 最终名单 = 各范围并集 - 排除 + 个别人
    refreshFinal() {
      const set = new Set();
      this.scopes.forEach(scope => {
        (scope.ids || []).forEach(id => {
          const key = String(id);
          if (!this.excludeSet.has(key)) set.add(key);
        });
      });
      this.individuals.forEach(item => {
        if (!this.excludeSet.has(item.id)) set.add(item.id);
      });
      this.finalIds = Array.from(set);
      this.finalCount = this.finalIds.length;
    },
    // ============ 提交 ============
    handleConfirm() {
      if (this.scopes.some(s => s.loading)) {
        this.$message({ type: 'warning', center: true, message: '正在获取选中范围的人员,请稍候' });
        return;
      }
      this.emitChange();
      this.visible = false;
      this.$message({ type: 'success', center: true, message: `已选择 ${this.finalCount} 人` });
    },
    emitChange() {
      // 名称与 ids 严格同序:范围里的人统一用范围名(后端会按 ids 生成 desc 存库)
      const nameMap = new Map();
      this.scopes.forEach(scope => {
        (scope.ids || []).forEach(id => nameMap.set(String(id), scope.label));
      });
      this.individuals.forEach(item => nameMap.set(item.id, item.name));
      const names = this.finalIds.map(id => nameMap.get(id) || '');
      this.$emit('change', { ids: this.finalIds.slice(), names });
    },
  },
};
</script>

<style lang="less" scoped>
.pp-trigger {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

/* 触发按钮与弹窗内的按钮/输入不受外层 el-form 的 disabled 注入影响(provide 已断开) */

.pp-summary {
  color: #606266;
}

.pp-name-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 420px;
}

.pp-wrap {
  width: 100%;
}

.pp-mobile-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;

  .el-select,
  .el-cascader {
    flex: 1;
    width: auto;
  }
}

.pp-body {
  display: flex;
}

.pp-tree {
  flex: 0 0 230px;
  width: 230px;
  border-right: 1px solid #ebeef5;
  padding-right: 10px;
}

.pp-tree-list {
  margin-top: 6px;
  max-height: 380px;
  overflow: auto;
}

.pp-tree-node {
  font-size: 13px;
}

.pp-tree-empty {
  padding: 10px;
  color: #909399;
  font-size: 12px;
}

.pp-list {
  flex: 1;
  min-width: 0;
  padding-left: 12px;
}

.pp-list-head {
  display: flex;
  align-items: center;
  gap: 8px;

  .pp-scope {
    font-weight: 600;
    margin-right: auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .pp-search {
    width: 170px;
    flex: 0 0 auto;
  }
}

.pp-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0;

  .pp-total {
    color: #909399;
    margin-right: auto;
  }
}

.pp-rows {
  min-height: 260px;
  max-height: 340px;
  overflow: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.pp-row {
  display: grid;
  grid-template-columns: 44px 1fr 150px;
  align-items: center;
  padding: 5px 8px;
  border-bottom: 1px solid #f5f7fa;
  font-size: 13px;

  &.is-picked {
    background: #ecf5ff;
  }
}

.pp-row-head {
  color: #909399;
  background: #fafafa;
  font-size: 12px;
  position: sticky;
  top: 0;
  z-index: 1;
}

.pp-cell-num {
  color: #606266;
}

.pp-row-empty {
  padding: 30px;
  text-align: center;
  color: #909399;
  font-size: 12px;
}

.pp-pager {
  margin-top: 8px;
  text-align: right;
}

.pp-picked {
  margin-top: 12px;
  border-top: 1px solid #ebeef5;
  padding-top: 10px;
  max-height: 132px;
  overflow: auto;
}

.pp-picked-head {
  color: #606266;
  margin-bottom: 6px;

  b {
    color: #409eff;
  }
}

.pp-excluded {
  color: #e6a23c;
}

.pp-picked-body {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.pp-picked-empty {
  color: #909399;
  font-size: 12px;
}

/* ============ 移动端:组织下拉 + 卡片列表 ============ */
.pp-mobile {
  .pp-body {
    display: block;
  }

  .pp-tree {
    display: none;
  }

  .pp-list {
    padding-left: 0;
  }

  .pp-list-head {
    flex-wrap: wrap;

    .pp-search {
      width: auto;
      flex: 1;
    }
  }

  .pp-actions {
    flex-wrap: wrap;
  }

  .pp-rows {
    max-height: 44vh;
  }

  .pp-row {
    grid-template-columns: 36px 1fr;
    row-gap: 2px;
    padding: 10px 8px;
    min-height: 52px;
  }

  .pp-row-head {
    display: none;
  }

  .pp-cell-num {
    grid-column: 2;
    color: #909399;
    font-size: 12px;
  }

  .pp-picked {
    max-height: 160px;
  }
}
</style>

<style lang="less">
/* 移动端级联面板:窄屏下三级菜单并排要放得下。
   popper 是 append-to-body 的,scoped 样式覆盖不到,所以放全局 */
.pp-cascader-popper .el-cascader-menu {
  min-width: 100px;
  max-width: 118px;
}

.pp-cascader-popper .el-cascader-menu__wrap {
  height: 230px;
}

.pp-cascader-popper .el-cascader-node {
  padding: 0 6px;
}

.pp-cascader-popper .el-cascader-node__label {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
